import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import type { JobApplication, SaveVacancyRequest, Vacancy } from '../api/types';
import { Alert, Spinner, formatDate } from '../components/common';
import { useAuth } from '../state/AuthContext';
import { useBranch } from '../state/BranchContext';

/**
 * Hiring: the adverts, and the people who answered them.
 *
 * Applications outlive their vacancy — closing an advert takes it off the site
 * but keeps everyone who applied, because they are the point of the exercise.
 * So the two lists are shown side by side rather than applications being
 * buried inside a vacancy that may no longer exist.
 */

const EMPLOYMENT: Record<string, string> = {
  FullTime: 'Full time',
  PartTime: 'Part time',
  Casual: 'Casual shifts',
  Learnership: 'Learnership',
};

const STATUSES = ['New', 'Shortlisted', 'Interviewed', 'Hired', 'Declined'] as const;

function statusTone(status: string) {
  if (status === 'Hired') return 'pill--good';
  if (status === 'Declined') return 'pill--bad';
  if (status === 'New') return 'pill--good';
  return 'pill--muted';
}

function blank(): SaveVacancyRequest {
  return { title: '', employment: 'FullTime', summary: '', description: '', branchId: null, closesOn: null };
}

export function StaffCareersPage() {
  const { customer: me } = useAuth();
  const { branches } = useBranch();
  const canManage = me?.role === 'Admin' || me?.role === 'SuperAdmin';

  const [vacancies, setVacancies] = useState<Vacancy[] | null>(null);
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [filter, setFilter] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<SaveVacancyRequest>(blank);

  const load = useCallback(async () => {
    try {
      const [v, a] = await Promise.all([api.vacanciesAll(), api.applications(filter)]);
      setVacancies(v);
      setApplications(a);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
      setEditing(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!vacancies) return <Spinner />;

  return (
    <section className="page">
      <div className="branches__head">
        <div>
          <h1>Hiring</h1>
          <p className="muted">
            Vacancies show on the careers page at e-smoothies.co.za. Anyone can apply without an
            account, and their CV is stored privately.
          </p>
        </div>
        {canManage && editing === null && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              setForm(blank());
              setEditing('new');
            }}
          >
            Advertise a job
          </button>
        )}
      </div>

      {error && <Alert>{error}</Alert>}

      {editing !== null && (
        <div className="panel">
          <h2 className="panel__title">{editing === 'new' ? 'A new advert' : 'Edit this advert'}</h2>

          <label className="field">
            <span>Job title</span>
            <input
              value={form.title}
              placeholder="Barista — Randburg Square"
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </label>

          <div className="form-row">
            <label className="field">
              <span>Employment</span>
              <select
                value={form.employment}
                onChange={(e) =>
                  setForm({ ...form, employment: e.target.value as SaveVacancyRequest['employment'] })
                }
              >
                {Object.entries(EMPLOYMENT).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Branch</span>
              <select
                value={form.branchId ?? ''}
                onChange={(e) => setForm({ ...form, branchId: e.target.value || null })}
              >
                <option value="">Any branch</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="field">
            <span>One line for the list</span>
            <input
              value={form.summary}
              placeholder="Make the drinks and work the counter. Training given."
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
            />
          </label>

          <label className="field">
            <span>The full advert</span>
            <textarea
              rows={6}
              value={form.description}
              placeholder="Hours, what the job involves, what you are looking for…"
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </label>

          <label className="field">
            <span>Closes on (optional)</span>
            <input
              type="date"
              value={form.closesOn ?? ''}
              onChange={(e) => setForm({ ...form, closesOn: e.target.value || null })}
            />
          </label>

          <div className="rolebtns">
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy || !form.title.trim() || !form.summary.trim() || !form.description.trim()}
              onClick={() =>
                run(() =>
                  editing === 'new' ? api.vacancyCreate(form) : api.vacancyUpdate(editing, form),
                )
              }
            >
              {busy ? 'Saving…' : editing === 'new' ? 'Advertise it' : 'Save changes'}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <h2 className="section-heading">Vacancies</h2>

      {vacancies.length === 0 && (
        <Alert tone="info">Nothing advertised yet. The careers page still takes general applications.</Alert>
      )}

      {vacancies.map((v) => (
        <div key={v.id} className={`panel ${v.isOpen ? '' : 'branch--closed'}`}>
          <h3 className="panel__title">
            {v.title}{' '}
            <span className={`pill ${v.isAcceptingApplications ? 'pill--good' : 'pill--muted'}`}>
              {v.isAcceptingApplications ? 'Taking applications' : 'Closed'}
            </span>
          </h3>
          <p className="muted small">
            {EMPLOYMENT[v.employment] ?? v.employment}
            {v.branchName ? ` · ${v.branchName}` : ' · any branch'}
            {v.closesOn ? ` · closes ${v.closesOn}` : ''} · {v.applicationCount} application
            {v.applicationCount === 1 ? '' : 's'}
          </p>
          <p className="muted small">{v.summary}</p>

          <div className="rolebtns">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setFilter(filter === v.id ? null : v.id)}
            >
              {filter === v.id ? 'Show all applications' : 'Show its applications'}
            </button>
            {canManage && (
              <>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={busy}
                  onClick={() => {
                    setForm({
                      title: v.title,
                      employment: v.employment,
                      summary: v.summary,
                      description: v.description,
                      branchId: v.branchId,
                      closesOn: v.closesOn,
                    });
                    setEditing(v.id);
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className={v.isOpen ? 'btn btn--danger btn--sm' : 'btn btn--ghost btn--sm'}
                  disabled={busy}
                  onClick={() => run(() => api.vacancySetOpen(v.id, !v.isOpen))}
                >
                  {v.isOpen ? 'Close this advert' : 'Reopen'}
                </button>
              </>
            )}
          </div>
        </div>
      ))}

      <h2 className="section-heading">
        Applications{filter ? ' for this vacancy' : ''} ({applications.length})
      </h2>

      {applications.length === 0 && <Alert tone="info">Nobody has applied yet.</Alert>}

      {applications.map((a) => (
        <div key={a.id} className="panel">
          <h3 className="panel__title">
            {a.fullName} <span className={`pill ${statusTone(a.status)}`}>{a.status}</span>
          </h3>
          <p className="muted small">
            <a href={`mailto:${a.email}`}>{a.email}</a> · <a href={`tel:${a.phone}`}>{a.phone}</a> ·{' '}
            {a.vacancyTitle ?? 'General application'} · applied {formatDate(a.appliedAt)}
          </p>
          {a.coverNote && <p className="muted small">“{a.coverNote}”</p>}
          {a.internalNote && <p className="muted small">Note: {a.internalNote}</p>}

          <div className="rolebtns">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() =>
                run(async () => {
                  await api.applicationCv(a.id, a.cvFileName);
                })
              }
            >
              Open CV ({Math.max(1, Math.round(a.cvSizeBytes / 1024))} KB)
            </button>
            {STATUSES.filter((s) => s !== a.status).map((s) => (
              <button
                key={s}
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={busy}
                onClick={() => run(() => api.applicationReview(a.id, s, a.internalNote))}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}
